import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import ctaBg from '../landing/assets/about_us_4.webp';
import iconApple from '../landing/assets/Apple.png';
import iconGoogle from '../landing/assets/Playstore.png';
import tennisTournamentIcon3 from '../landing/assets/svgs/dynamic_icon.svg';
import tennisTournamentIcon4 from '../landing/assets/svgs/player_team_icon.svg';
import tennisTournamentIcon1 from '../landing/assets/svgs/scheduling_icon.svg';
import tennisTournamentIcon2 from '../landing/assets/svgs/scoring_icon.svg';
import tennisTournamentFeaturesBg from '../landing/assets/tennis-tournament/Tennis3.webp';
import tennisTournamentWhyBg from '../landing/assets/tennis-tournament/Tennis6.webp';
import tennisTournamentHeroBg from '../landing/assets/tennis-tournament/tennishero.webp';
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

const FAQs = [
    {
        q: "What is tennis tournament software, and how does it work?",
        a: <>Tennis tournament software is a digital platform designed to manage tennis tournaments from start to end. It helps organizers create schedules, manage players, control brackets, track scores, and display standings that can be shared easily across the site for an online tennis tournament.</>,
    },
    {
        q: "Can players register and sign up online?",
        a: <>Yes. Every tennis event gets a public registration page where players sign up online — choosing their division, selecting a partner for doubles, answering custom questions, accepting waivers and paying entry fees via Stripe.</>,
    },
    {
        q: "Does this tennis tournament software support live scoring and brackets?",
        a: <>Yes, this tennis tournament software includes <a href="https://scorepal.co/live-scoring/" className={"not-hover:underline"}>live scoring</a> features that update match scores and brackets in real time. Players and spectators can find more information and follow matches, scores, and standings directly on the site without delays.</>,
    },
    {
        q: "Can this platform be used as tennis management software for leagues?",
        a: <>Absolutely. The platform works as tennis management software and sports league software, making it ideal for clubs, schools, and associations. It also supports tournament league software needs, including recurring events, multiple divisions, and league standings.</>,
    },
    {
        q: "How does the software handle data, cookies, and analytics?",
        a: <>The software may collect and process personal data for basic functionality such as login, scheduling, and displaying results. Cookies and similar technologies are used to track visitor statistics on our website, improve performance, and support basic analytics and personalized ads, with clear permission and settings controls.</>,
    },
    {
        q: "Is this an online tennis tournament platform that works on mobile devices?",
        a: <>Yes, this online tennis tournament platform version is fully mobile-friendly. Users can access play schedules, scores, standings, matches, and settings from any device, making it easy to manage and follow tournaments on the go.</>,
    },
    {
        q: "What features make this tournament software easy to use?",
        a: <>This tournament software provides simple setup, customizable features, clear brackets, live scoring, sports analytics, and strong support resources. It helps organizers manage tennis tournaments efficiently while delivering a smooth experience for players and the wider tennis community.</>,
    },
];

export const Route = createFileRoute('/tennis-tournament-software')({
    component: RouteComponent,
    head:      () => ({
        meta:  [
            {
                title: 'Tennis Tournament Software for Clubs & Organizers | Automate Matches & Scoring',
            },
            {
                name: 'description',
                content:
                      'Tennis tournament software for clubs with automated brackets, live scoring, tennis league software & scoring system. Simplify management.',
            },
        ],
        links: [
            {
                rel:  'canonical',
                href: `${config.publicUrl}/tennis-tournament-software/`,
            },
            {
                rel:  'preload',
                as:   'image',
                href: tennisTournamentHeroBg,
            },
        ],
    }),
})

function RouteComponent() {
    const features = [
        {
            icon:        tennisTournamentIcon4,
            title:       'Online Registration & Signup',
            description: 'Give every event a public registration page where players sign up online — with division selection, partner choice for doubles, custom questions, waivers and entry-fee payments handled through Stripe.',
        },
        {
            icon:        tennisTournamentIcon1,
            title:       'Effortless Match Scheduling',
            description: (
                             <>
                                 Create accurate, conflict-free schedules in minutes. {' '}
                                 <a
                                     href="https://scorepal.co/sports-analytics-software-for-clubs/"
                                     className="not-hover:underline"
                                 >

                                     ScorePal
                                 </a>{' '}
                                 automatically accounts for court availability, match duration, and player restrictions to keep your event running smoothly.
                             </>
                         ),
        },
        {
            icon:        tennisTournamentIcon3,
            title:       'Professional Draw Creation',
            description: 'Build clean, balanced tournament draws with support for single-elimination, double-elimination, round robin, and more. ScorePal ensures your brackets stay organized from the first serve to the final match.',
        },
        {
            icon:        tennisTournamentIcon2,
            title:       'Real-Time Scoring & Results',
            description: 'Enter scores on the fly and let ScorePal instantly update draws, leaderboards, and brackets. Players, coaches, and spectators always have access to the latest results.',
        },
        {
            icon:        tennisTournamentIcon4,
            title:       'Easy Player Management ',
            description: 'Find and track registrations, seedings, rankings, and divisions all in one place. ScorePal, a tennis management software, centralizes player data to make administration faster and more accurate.',
        },
    ];

    const whyChooseItems = [
        'Fast, automated scheduling',
        'Intuitive scoring and live updates',
        'Customizable tournament formats',
        'Ideal for clubs, schools, and associations',
        'Seamless player and roster management',
    ];

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context":   "https://schema.org",
                "@type":      "FAQPage",
                "mainEntity": [
                    {
                        "@type":          "Question",
                        "name":           "What is tennis tournament software, and how does it work?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Tennis tournament software is a digital platform designed to manage tennis tournaments from start to end. It helps organizers create schedules, manage players, control brackets, track scores, and display standings that can be shared easily across the site for an online tennis tournament."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can players register and sign up online?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes. Every tennis event gets a public registration page where players sign up online, choosing their division, selecting a partner for doubles, answering custom questions, accepting waivers and paying entry fees via Stripe."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Does this tennis tournament software support live scoring and brackets?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, this tennis tournament software includes live scoring features that update match scores and brackets in real time. Players and spectators can find more information and follow matches, scores, and standings directly on the site without delays."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can this platform be used as tennis management software for leagues?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Absolutely. The platform works as tennis management software and sports league software, making it ideal for clubs, schools, and associations. It also supports tournament league software needs, including recurring events, multiple divisions, and league standings."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "How does the software handle data, cookies, and analytics?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "The software may collect and process personal data for basic functionality such as login, scheduling, and displaying results. Cookies and similar technologies are used to track visitor statistics on our website, improve performance, and support basic analytics and personalized ads, with clear permission and settings controls."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Is this an online tennis tournament platform that works on mobile devices?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, this online tennis tournament platform version is fully mobile-friendly. Users can access play schedules, scores, standings, matches, and settings from any device, making it easy to manage and follow tournaments on the go."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "What features make this tournament software easy to use?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "This tournament software provides simple setup, customizable features, clear brackets, live scoring, sports analytics, and strong support resources. It helps organizers manage tennis tournaments efficiently while delivering a smooth experience for players and the wider tennis community."
                        }
                    }
                ]
            }} />
            <JsonLd id={`page${Route.id}/app`} item={{
                "@context":            "https://schema.org",
                "@type":               "SoftwareApplication",
                "name":                "Tennis Tournament Software",
                "applicationCategory": "SportsApplication",
                "url":                 "https://scorepal.co/tennis-tournament-software/",
                "description":         "Run professional tennis tournaments with automated draws, efficient scheduling, player management, and real-time live scoring powered by ScorePal. Proven performance over the past 2 years.",
                "operatingSystem":     ["Web", "Android", "iOS"],
                "offers":              {
                    "@type":         "Offer",
                    "price":         "0",
                    "priceCurrency": "USD"
                }
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Tennis Tournament Software", item: "https://scorepal.co/tennis-tournament-software/"}]} />
            <section className="relative overflow-hidden p-4">
                <div className="relative flex min-h-screen flex-col justify-between overflow-hidden rounded-4xl bg-[#EDEEF4]">
                    <img
                        src={tennisTournamentHeroBg}
                        alt="Tennis tournament software"
                        fetchPriority="high"
                        loading="eager"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover object-top saturate-[0.87]"
                        width={1920}
                        height={1080}
                    />
                    <div className="absolute inset-0 bg-linear-to-r from-black/60 via-black/30 to-transparent rounded-4xl" />

                    <div className="relative z-20">
                        <LandingNav />
                    </div>


                    <div className="container mx-auto px-6 lg:px-12 pt-8 flex-1 flex flex-col justify-center relative z-10">
                        <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-4">
                            Tennis Tournament <br />Software
                        </h1>

                        <p className="font-inter text-white/90 text-base lg:text-xl mb-8 max-w-2xl leading-relaxed">
                            Tennis tournament software allows clubs to manage matches easily, track scores in real time, and maintain structured tournament flow.
                        </p>

                        <Link
                            to={"/signup/player/" as any}
                            className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>

                        <div className="flex flex-wrap gap-4 mt-16 lg:mt-60">
                            <a
                                href="https://play.google.com/store/apps/details?id=co.scorepal.mobile&pcampaignid=web_share"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-100 transition">
                                    <img src={iconGoogle} alt="Google Play" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">GET IT ON</div>
                                        <div className="text-white font-semibold text-sm">Google Play</div>
                                    </div>
                                </div>
                            </a>

                            <a
                                href="https://apps.apple.com/us/app/score-pal/id6745509167"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-900 transition">
                                    <img src={iconApple} alt="App Store" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">Download on the</div>
                                        <div className="text-white font-semibold text-sm">App Store</div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                                Smart, Seamless Management<br />for <span className="text-[#082F8A]">Every Tennis Event</span>
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                                Take your tournaments to the next level with ScorePal’s tennis tournament software—the complete solution for scheduling matches, managing draws, tracking scores, and organizing player data. Whether you’re running a club tournament, school championship, or community event, ScorePal simplifies every step of the process.
                            </p>
                        </div>

                        <div className="hidden lg:block">
                            <img
                                src={tennisTournamentFeaturesBg}
                                alt="Sports analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
                        {features.map((feature, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-[#EDEEF4] flex items-center justify-center shrink-0">
                                        <img src={feature.icon} alt="" className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-inter text-[#082F8A] font-semibold text-base">
                                        {feature.title}
                                    </h3>
                                </div>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="flex justify-center">
                <Link
                    to={"/signup/player/" as any}
                    className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                >
                    <span className="font-inter text-base font-medium">Lets Get Started</span>
                    <div className="bg-[#082F8A] rounded-full p-1">
                        <ArrowUpRightIcon className="text-white" size={28} />
                    </div>
                </Link>
            </div>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                                Why Do Organizers Choose<br /> ScorePal Tennis Tournament Software?
                            </h2>

                            <ul className="space-y-4">
                                {whyChooseItems.map((item, index) => (
                                    <li key={index} className="flex items-center gap-4">
                                        <CheckBadgeIcon size={24} />
                                        <span className="font-inter text-[#1a1a2e] text-base">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="hidden lg:block">
                            <img
                                src={tennisTournamentWhyBg}
                                alt="Why choose ScorePal Analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>

                        <div className="block lg:hidden">
                            <img
                                src={tennisTournamentWhyBg}
                                alt="Why choose ScorePal Analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-8 lg:py-16">
                <div className="container mx-auto px-6 lg:px-12">
                    <div
                        className="rounded-3xl py-16 lg:py-24 px-8 text-center bg-top bg-cover bg-no-repeat"
                        style={{backgroundImage: `url(${ctaBg})`}}
                    >
                        <h2 className="font-bebas text-white uppercase text-[clamp(28px,4vw,48px)] leading-tight font-bold tracking-wide mb-4">
                            Streamline Your Tennis Events with ScorePal
                        </h2>
                        <p className="font-inter text-white/90 text-base lg:text-lg mb-8 max-w-3xl mx-auto leading-relaxed">
                            ScorePal, tennis scheduling software, gives tournament organizers the tools to deliver professional, stress-free tennis events. Reduce admin time, eliminate errors, and create a great experience for everyone on and off the game court.
                            <br /><br />
                            Upgrade your tennis tournament management with ScorePal tennis tournament software today.
                        </p>
                        <Link
                            to={"/signup/player/" as any}
                            className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                    <FaqComponent title={"Faqs About Tennis Tournament Software"} faqs={FAQs} />
                </div>
            </section>

            <FooterSection />
        </div>
    );
}
